import { default as WJElement } from '../wje-element/element.js';
import styles from './styles/styles.css?inline';

/**
 * `Toolbar` is a custom web component that represents a toolbar.
 * @summary This element represents a toolbar.
 * @documentation https://elements.webjet.sk/components/toolbar
 * @status stable
 * @augments {WJElement}
 * @csspart native - The native toolbar wrapper.
 * @slot start - The start slot for the toolbar.
 * @slot end - The end slot for the toolbar.
 * @cssproperty [--wje-toolbar-background=var(--wje-background)] - Specifies the background color of the toolbar. Accepts any valid CSS color value, such as `hex`, `rgb`, or `CSS variable`.
 * @cssproperty [--wje-toolbar-height=auto] - Defines the height of the toolbar. If set to `auto`, the height adjusts based on the content.
 * @cssproperty [--wje-toolbar-min-height=70px] - Sets the minimum height of the toolbar. Ensures the toolbar maintains a consistent minimum size.
 * @cssproperty [--wje-toolbar-padding-top=1rem] - Specifies the padding at the top of the toolbar. Accepts any valid CSS length unit.
 * @cssproperty [--wje-toolbar-padding-bottom=1rem] - Specifies the padding at the bottom of the toolbar. Helps create spacing between the content and the bottom edge.
 * @cssproperty [--wje-toolbar-padding-inline=1.5rem] - Defines the horizontal padding (left and right) of the toolbar. Creates consistent spacing on both sides.
 * @cssproperty [--wje-toolbar-margin-block=0] - Defines the vertical margin outside the toolbar. Accepts any valid CSS length value.
 * @cssproperty [--wje-toolbar-margin-inline=0] - Defines the horizontal margin outside the toolbar. Accepts any valid CSS length value.
 * @cssproperty [--wje-toolbar-border-color=var(--wje-border-color)] - Sets the color of the toolbar's border. Accepts any valid CSS color value.
 * @cssproperty [--wje-toolbar-top=0] - Specifies the vertical position of the toolbar relative to its container. Useful for fixed or sticky toolbars.
 * @tag wje-toolbar
 */

export default class Toolbar extends WJElement {
    /**
     * Creates an instance of Toolbar.
     */
    constructor() {
        super();
        this._breadcrumbState = new WeakMap();
        this._responsiveFrame = null;
    }

    /**
     * The class name for the component.
     * @type {string}
     */
    className = 'Toolbar';

    /**
     * Returns the CSS stylesheet for the component.
     * @static
     * @returns {CSSStyleSheet} The CSS stylesheet
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Returns the list of observed attributes.
     * @static
     * @returns {Array} An empty array
     */
    static get observedAttributes() {
        return [];
    }

    /**
     * Sets up the attributes for the component.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.syncAria();
    }

    /**
     * Draws the component for the toolbar.
     * @returns {object} Document fragment
     */
    draw() {
        let fragment = document.createDocumentFragment();

        let native = document.createElement('div');
        native.setAttribute('part', 'native');
        native.classList.add('native-toolbar');

        let start = document.createElement('slot');
        start.setAttribute('name', 'start');

        let end = document.createElement('slot');
        end.setAttribute('name', 'end');

        native.appendChild(start);
        native.appendChild(end);
        fragment.appendChild(native);

        this.native = native;
        this.startSlot = start;
        this.endSlot = end;

        return fragment;
    }

    /**
     * Initializes responsive layout observers.
     */
    afterDraw() {
        this.onSlotChange = () => {
            this._breadcrumbState = new WeakMap();
            this.scheduleResponsiveLayout();
        };
        this.startSlot?.addEventListener('slotchange', this.onSlotChange);
        this.endSlot?.addEventListener('slotchange', this.onSlotChange);

        if (typeof ResizeObserver === 'function') {
            this._resizeObserver = new ResizeObserver(() => this.scheduleResponsiveLayout());
            this._resizeObserver.observe(this.native || this);
        }

        this.scheduleResponsiveLayout();
    }

    /**
     * Cleans up responsive layout observers.
     */
    afterDisconnect() {
        this.startSlot?.removeEventListener('slotchange', this.onSlotChange);
        this.endSlot?.removeEventListener('slotchange', this.onSlotChange);
        this._resizeObserver?.disconnect();

        if (this._responsiveFrame) {
            cancelAnimationFrame(this._responsiveFrame);
            this._responsiveFrame = null;
        }
    }

    /**
     * Sync ARIA attributes on host.
     */
    syncAria() {
        if (!this.hasAttribute('role')) {
            this.setAriaState({ role: 'toolbar' });
        }

        const ariaLabel = this.getAttribute('aria-label');
        const label = this.getAttribute('label');
        if (!ariaLabel && label) {
            this.setAriaState({ label });
        }
    }

    /**
     * Schedules responsive layout recalculation.
     */
    scheduleResponsiveLayout() {
        if (this._responsiveFrame) return;

        this._responsiveFrame = requestAnimationFrame(() => {
            this._responsiveFrame = null;
            this.updateResponsiveLayout();
        });
    }

    /**
     * Updates slotted breadcrumbs and actions to fit the toolbar width.
     * @returns {Promise<void>}
     */
    async updateResponsiveLayout() {
        const action = this.getToolbarAction();
        const breadcrumbs = this.getBreadcrumbs();
        const isSelfManagedAction = this.isSelfManagedAction(action);
        const isSelfManagedBreadcrumbs = this.isSelfManagedBreadcrumbs(breadcrumbs, action);

        if (!this.native || (!action && !breadcrumbs)) return;

        const toolbarWidth = this.native.getBoundingClientRect().width;
        if (!toolbarWidth) return;

        const actionMetrics = action && !isSelfManagedAction ? action.measureActionMetrics?.() : null;
        const breadcrumbMetrics =
            breadcrumbs && !isSelfManagedBreadcrumbs
                ? await this.measureBreadcrumbs(breadcrumbs)
                : null;

        let visibleActions = actionMetrics?.count || 0;
        let compactBreadcrumbs = false;

        if (actionMetrics && breadcrumbMetrics) {
            for (let count = actionMetrics.count; count >= 0; count--) {
                const actionWidth = actionMetrics.getWidthForCount(count);
                const availableBreadcrumbWidth = toolbarWidth - actionWidth;

                if (breadcrumbMetrics.fullWidth <= availableBreadcrumbWidth) {
                    visibleActions = count;
                    compactBreadcrumbs = false;
                    break;
                }

                if (breadcrumbMetrics.compactWidth <= availableBreadcrumbWidth) {
                    visibleActions = count;
                    compactBreadcrumbs = true;
                    break;
                }

                if (count === 0) {
                    visibleActions = 0;
                    compactBreadcrumbs = true;
                }
            }
        } else if (actionMetrics) {
            visibleActions = this.getVisibleActionsForWidth(actionMetrics, toolbarWidth);
        } else if (breadcrumbMetrics) {
            compactBreadcrumbs = breadcrumbMetrics.fullWidth > toolbarWidth;
        }

        if (isSelfManagedAction) {
            this.clearVisibleActions(action);
        } else {
            this.setVisibleActions(action, visibleActions);
        }

        if (!isSelfManagedBreadcrumbs) {
            this.setBreadcrumbCompactState(breadcrumbs, compactBreadcrumbs);
        }
    }

    /**
     * Measures breadcrumbs in their full state.
     * @param {HTMLElement} breadcrumbs Breadcrumbs component.
     * @returns {Promise<{count: number, fullWidth: number, compactWidth: number}>}
     */
    async measureBreadcrumbs(breadcrumbs) {
        const items = breadcrumbs.getBreadcrumbs?.() || [];
        const count = items.length;

        if (count === 0) {
            return { count: 0, fullWidth: 0, compactWidth: 0 };
        }

        const cachedState = this.ensureBreadcrumbState(breadcrumbs, count);
        if (cachedState.count === count && cachedState.fullWidth) {
            return {
                count,
                fullWidth: cachedState.fullWidth,
                compactWidth: cachedState.compactWidth,
            };
        }

        this.setBreadcrumbMaxItems(breadcrumbs, count);

        await breadcrumbs.updateComplete;
        await this.nextFrame();

        const itemWidths = items.map((item) => item.getBoundingClientRect().width);
        const fullWidth = itemWidths.reduce((sum, width) => sum + width, 0);
        const state = this.ensureBreadcrumbState(breadcrumbs, count);
        const before = Math.max(1, breadcrumbs.itemsBeforeCollapse || 1);
        const after = Math.max(1, breadcrumbs.itemsAfterCollapse || 1);
        const visibleBefore = itemWidths.slice(0, before).reduce((sum, width) => sum + width, 0);
        const visibleAfter = itemWidths.slice(Math.max(count - after, before)).reduce((sum, width) => sum + width, 0);
        const compactWidth = count > state.compactMaxItems
            ? visibleBefore + visibleAfter + 48
            : fullWidth;

        state.count = count;
        state.fullWidth = fullWidth;
        state.compactWidth = compactWidth;

        return { count, fullWidth, compactWidth };
    }

    /**
     * Stores original breadcrumb settings used as responsive compact target.
     * @param {HTMLElement} breadcrumbs Breadcrumbs component.
     * @param {number} count Number of breadcrumb elements currently in the trail.
     * @returns {{compactMaxItems: number}}
     */
    ensureBreadcrumbState(breadcrumbs, count) {
        if (!this._breadcrumbState.has(breadcrumbs)) {
            const attr = breadcrumbs.getAttribute('max-items');
            const parsed = attr === null ? NaN : Number(attr);
            const compactMaxItems = Number.isFinite(parsed) && parsed > 0 ? parsed : Math.min(3, count);

            this._breadcrumbState.set(breadcrumbs, {
                compactMaxItems: Math.max(1, Math.min(compactMaxItems, count)),
            });
        }

        return this._breadcrumbState.get(breadcrumbs);
    }

    /**
     * Applies the compact or full breadcrumb state.
     * @param {HTMLElement|null} breadcrumbs Breadcrumbs component.
     * @param {boolean} compact Whether compact mode should be used.
     */
    setBreadcrumbCompactState(breadcrumbs, compact) {
        if (!breadcrumbs) return;

        const count = breadcrumbs.getBreadcrumbs?.().length || 0;
        const state = this.ensureBreadcrumbState(breadcrumbs, count);
        const nextMaxItems = compact ? state.compactMaxItems : count;

        this.setBreadcrumbMaxItems(breadcrumbs, nextMaxItems);
    }

    /**
     * Sets breadcrumb max-items only when it changed.
     * @param {HTMLElement} breadcrumbs Breadcrumbs component.
     * @param {number} value The max item count.
     */
    setBreadcrumbMaxItems(breadcrumbs, value) {
        if (!breadcrumbs || !value) return;

        if (+breadcrumbs.getAttribute('max-items') === value) {
            breadcrumbs.updateCollapse?.();
            return;
        }

        breadcrumbs.setAttribute('max-items', value);
        breadcrumbs.updateCollapse?.();
    }

    /**
     * Finds how many actions fit into the available width.
     * @param {object} actionMetrics Measured action metrics.
     * @param {number} width Available width.
     * @returns {number}
     */
    getVisibleActionsForWidth(actionMetrics, width) {
        for (let count = actionMetrics.count; count >= 0; count--) {
            if (actionMetrics.getWidthForCount(count) <= width) return count;
        }

        return 0;
    }

    /**
     * Applies visible action count.
     * @param {HTMLElement|null} action Toolbar action component.
     * @param {number} count Visible action count.
     */
    setVisibleActions(action, count) {
        if (!action) return;

        if (+action.getAttribute('visible-items') === count) {
            return;
        }

        action.setAttribute('visible-items', count);
        action.applyOverflow?.();
    }

    /**
     * Clears toolbar-managed visible action state when actions manage themselves.
     * @param {HTMLElement|null} action Toolbar action component.
     */
    clearVisibleActions(action) {
        if (!action?.hasAttribute?.('visible-items')) return;

        action.removeAttribute('visible-items');
    }

    /**
     * Returns the slotted toolbar action.
     * @returns {HTMLElement|null}
     */
    getToolbarAction() {
        return this.endSlot?.assignedElements?.().find((el) => el.tagName?.toLowerCase() === 'wje-toolbar-action') || null;
    }

    /**
     * Returns the slotted breadcrumbs.
     * @returns {HTMLElement|null}
     */
    getBreadcrumbs() {
        return this.startSlot?.assignedElements?.().find((el) => el.tagName?.toLowerCase() === 'wje-breadcrumbs') || null;
    }

    /**
     * Returns whether toolbar actions are managed by their own breakpoint logic.
     * @param {HTMLElement|null} action Toolbar action component.
     * @returns {boolean}
     */
    isSelfManagedAction(action) {
        return !!action?.getAttribute?.('breakpoint');
    }

    /**
     * Returns whether breadcrumb collapse is managed by its own breakpoint logic.
     * @param {HTMLElement|null} breadcrumbs Breadcrumbs component.
     * @returns {boolean}
     */
    isSelfManagedBreadcrumbs(breadcrumbs, action) {
        return (
            !!breadcrumbs?.getAttribute('breakpoint') ||
            (breadcrumbs?.hasAttribute?.('max-items') && !!action?.getExistingDropdown?.())
        );
    }

    /**
     * Waits for one animation frame.
     * @returns {Promise<void>}
     */
    nextFrame() {
        return new Promise((resolve) => requestAnimationFrame(resolve));
    }
}
